/**
 * Curation Queue — Dedup Review Daily Driver
 * Route: /curation
 *
 * Keyboard-first queue for clearing the reconciliation_staging dedup review
 * docket. Routes through the same choke point as the MCP dedup tools.
 * Verbs: corroborate (a), distinct (r), defer (d).
 * Dispatch: EX-P1-FIX-1.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Badge, Button, Spinner } from 'flowbite-react';
import {
  HiCheck,
  HiPlusSm,
  HiRefresh,
  HiExclamation,
  HiClock,
  HiChevronDown,
  HiChevronUp,
  HiArrowSmUp,
} from 'react-icons/hi';
import { dedupApi } from '../api/dedup';
import { standardsApi } from '../api/standards';
import type { DedupReviewItem, DedupEvidence, SupersedeClaimItem, SourceGroup } from '../api/dedup';

const DISTINCT_REASONS = [
  { value: 'genuinely_new', label: 'Genuinely New', key: '1' },
  { value: 'different_scope', label: 'Different Scope', key: '2' },
  { value: 'complementary', label: 'Complementary', key: '3' },
  { value: 'supersedes', label: 'Supersedes Match', key: '4' },
] as const;

const DEFER_PRESETS = [
  { label: '1 day', days: 1, key: '1' },
  { label: '3 days', days: 3, key: '2' },
  { label: '1 week', days: 7, key: '3' },
  { label: '2 weeks', days: 14, key: '4' },
] as const;

const NOISE_REASONS = [
  { value: 'session_chatter', label: 'Session Chatter', key: '1' },
  { value: 'too_vague', label: 'Too Vague', key: '2' },
  { value: 'already_covered', label: 'Already Covered', key: '3' },
  { value: 'not_actionable', label: 'Not Actionable', key: '4' },
] as const;

const TOPICS = [
  { id: 'adp-integration', label: 'ADP Integration', key: '1' },
  { id: 'datapass', label: 'DataPass', key: '2' },
  { id: 'flux-platform-contract', label: 'Flux Platform', key: '3' },
] as const;

const RETIRE_REASONS = [
  { value: 'outdated', label: 'Outdated', key: '1' },
  { value: 'wrong', label: 'Wrong / Incorrect', key: '2' },
  { value: 'duplicate_noise', label: 'Duplicate Noise', key: '3' },
  { value: 'not_actionable', label: 'Not Actionable', key: '4' },
] as const;

type InlineMode = null | 'distinct' | 'defer' | 'topic' | 'noise' | 'retire';

function formatScore(score: number | undefined | null): string {
  if (score == null) return '—';
  return `${Math.round(score * 100)}%`;
}

function bandColor(band: string): string {
  switch (band) {
    case 'auto_corroborate': return 'success';
    case 'review': return 'warning';
    case 'new': return 'info';
    default: return 'gray';
  }
}

function methodBadge(method: string): string {
  switch (method) {
    case 'semantic': return 'Semantic';
    case 'trigram': return 'Trigram';
    case 'identity': return 'Identity';
    default: return method || 'Unknown';
  }
}

function parseVerdictReason(reason: string | null): { verb: string; detail: string } | null {
  if (!reason) return null;
  if (reason.startsWith('dedup-review-corroborate:')) {
    const parts = reason.slice('dedup-review-corroborate:'.length).split(':');
    const method = parts[0] || '';
    const score = parts[1] ? `${Math.round(parseFloat(parts[1]) * 100)}%` : '';
    return { verb: 'corroborate', detail: [method, score].filter(Boolean).join(' ') };
  }
  if (reason.startsWith('dedup-review-distinct:')) {
    return { verb: 'distinct', detail: '' };
  }
  if (reason.startsWith('dedup-review-deferred:')) {
    return { verb: 'deferred', detail: reason.slice('dedup-review-deferred:'.length).split(':')[1] || '' };
  }
  if (reason.startsWith('dedup-review-routed:')) {
    const dest = reason.split(':')[1] || '';
    return { verb: 'routed', detail: dest };
  }
  if (reason.startsWith('dedup-review-rejected:')) {
    const parts = reason.split(':');
    return { verb: 'rejected', detail: parts.slice(2).join(':') || parts[1] || '' };
  }
  if (reason.startsWith('dedup-review-admit-topic:')) {
    const topicId = reason.split(':')[1] || '';
    return { verb: 'admit_topic', detail: topicId };
  }
  return null;
}

function verdictColor(verb: string): string {
  switch (verb) {
    case 'corroborate': return 'success';
    case 'distinct': return 'indigo';
    case 'deferred': return 'warning';
    case 'routed': return 'purple';
    case 'admit_topic': return 'blue';
    case 'rejected': return 'failure';
    default: return 'gray';
  }
}

function verdictLabel(verb: string): string {
  switch (verb) {
    case 'corroborate': return 'Corroborated';
    case 'distinct': return 'Distinct';
    case 'deferred': return 'Deferred';
    case 'routed': return 'Routed to Mimir';
    case 'admit_topic': return 'Admitted to Topic';
    case 'rejected': return 'Noise';
    default: return verb;
  }
}

type Verdict = 'corroborate' | 'distinct' | 'refine' | 'defer'
             | 'route_mimir' | 'admit_topic' | 'reject_noise';

interface VerdictAvailability {
  allowed: Verdict[];
  batchAllowed: Verdict[];
  remapped: Partial<Record<Verdict, Verdict>>;
  reason: Partial<Record<Verdict, string>>;
}

function availableVerdicts(
  item: DedupReviewItem,
  statusFilter: string
): VerdictAvailability {
  const result: VerdictAvailability = {
    allowed: [],
    batchAllowed: [],
    remapped: {},
    reason: {},
  };

  const isActionable = statusFilter === 'pending' || statusFilter === 'pending_admission';
  if (!isActionable) {
    const all: Verdict[] = ['corroborate', 'distinct', 'refine', 'defer', 'route_mimir', 'admit_topic', 'reject_noise'];
    for (const v of all) result.reason[v] = `Cannot act on ${statusFilter} items`;
    return result;
  }

  const isSoakReview = item.operation_type === 'soak_review';
  const hasTarget = !!item.target?.element;

  result.allowed.push('corroborate', 'distinct', 'defer', 'route_mimir', 'admit_topic', 'reject_noise');
  result.batchAllowed.push('corroborate', 'distinct', 'defer', 'reject_noise');
  // Batch refine excluded — 16.3% scope-narrowing rate when unsupervised

  if (isSoakReview) {
    result.remapped.corroborate = 'distinct';
  }

  if (isSoakReview || !hasTarget) {
    result.reason.refine = 'No matched standard to supersede';
  } else {
    result.allowed.push('refine');
  }

  return result;
}

const CurationQueue: React.FC = () => {
  const [items, setItems] = useState<DedupReviewItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActioning, setIsActioning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const [focusIndex, setFocusIndex] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [inlineMode, setInlineMode] = useState<InlineMode>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [counts, setCounts] = useState<Record<string, number>>({});

  const [viewMode, setViewMode] = useState<'flat' | 'source'>('flat');
  const [sourceGroups, setSourceGroups] = useState<SourceGroup[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const [typeFilter, setTypeFilter] = useState<'all' | 'dedup_review' | 'soak_review'>('all');

  const [supersedeMode, setSupersedeMode] = useState(false);
  const [supersedeClaims, setSupersedeClaims] = useState<SupersedeClaimItem[]>([]);
  const [supersedeCounts, setSupersedeCounts] = useState<Record<string, number>>({});

  useEffect(() => { loadQueue(); }, [statusFilter, supersedeMode, viewMode]);

  useEffect(() => {
    dedupApi.listSupersede('pending', 1).then(response => {
      if (response.success && response.data) {
        const payload = (response.data as any).data || response.data;
        setSupersedeCounts(payload.counts || {});
      }
    }).catch(() => {});
  }, []);

  const loadQueue = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (supersedeMode) {
        const response = await dedupApi.listSupersede(statusFilter);
        if (response.success && response.data) {
          const payload = (response.data as any).data || response.data;
          setSupersedeClaims(payload.items || []);
          setSupersedeCounts(payload.counts || {});
          setItems([]);
          setCounts({});
          setSourceGroups([]);
        } else {
          setError(response.error || 'Failed to load supersede claims');
        }
      } else if (viewMode === 'source') {
        const response = await dedupApi.listGrouped(statusFilter);
        if (response.success && response.data) {
          const payload = (response.data as any).data || response.data;
          const grps = (payload.groups || []) as SourceGroup[];
          setSourceGroups(grps);
          const allItems = grps.flatMap((g: SourceGroup) => g.items);
          setItems(allItems);
          setCounts(payload.counts || {});
          setSupersedeClaims([]);
          setSupersedeCounts({});
          if (grps.length > 0) {
            setExpandedGroups(new Set([grps[0].source_project]));
          }
        } else {
          setError(response.error || 'Failed to load grouped queue');
        }
      } else {
        const response = await dedupApi.list(statusFilter);
        if (response.success && response.data) {
          const payload = (response.data as any).data || response.data;
          setItems(payload.items || []);
          setCounts(payload.counts || {});
          setSupersedeClaims([]);
          setSupersedeCounts({});
          setSourceGroups([]);
        } else {
          setError(response.error || 'Failed to load dedup review queue');
        }
      }
      setFocusIndex(0);
      setSelected(new Set());
      setInlineMode(null);
    } catch {
      setError('Failed to load curation queue');
    } finally {
      setIsLoading(false);
    }
  };

  const sourceStats = useMemo(() => {
    const stats: Record<string, number> = {};
    for (const item of items) {
      const rail = item.evidence?.dedup_method || item.candidate?.source_label || 'unknown';
      stats[rail] = (stats[rail] || 0) + 1;
    }
    return stats;
  }, [items]);

  const typeCounts = useMemo(() => {
    const tc: Record<string, number> = { dedup_review: 0, soak_review: 0 };
    for (const item of items) {
      const ot = item.operation_type || 'dedup_review';
      tc[ot] = (tc[ot] || 0) + 1;
    }
    return tc;
  }, [items]);

  const filtered = useMemo(() =>
    items.filter(item => {
      if (typeFilter !== 'all' && item.operation_type !== typeFilter) return false;
      if (sourceFilter === 'all') return true;
      const rail = item.evidence?.dedup_method || item.candidate?.source_label || 'unknown';
      return rail === sourceFilter;
    }),
    [items, sourceFilter, typeFilter]
  );

  const showToast = useCallback((text: string, type: 'success' | 'error' = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const removeCard = useCallback((id: string) => {
    setItems(prev => prev.filter(item => item.staging_id !== id));
    setSelected(prev => { const next = new Set(prev); next.delete(id); return next; });
  }, []);

  const doAction = useCallback(async (
    item: DedupReviewItem,
    verdict: Verdict,
    extra?: { defer_reason?: string; defer_until?: string; reason?: string; topic_id?: string }
  ) => {
    setIsActioning(item.staging_id);
    try {
      const response = await dedupApi.resolve({
        staging_id: item.staging_id,
        verdict,
        ...extra,
      });
      if (response.success) {
        removeCard(item.staging_id);
        setCounts(prev => {
          const next = { ...prev };
          if (statusFilter === 'pending_admission') {
            if (next.pending_admission != null) next.pending_admission = Math.max(0, (next.pending_admission || 0) - 1);
          } else {
            if (next.pending != null) next.pending = Math.max(0, (next.pending || 0) - 1);
          }
          if (verdict === 'defer') next.deferred = (next.deferred || 0) + 1;
          else if (verdict === 'reject_noise') next.rejected = (next.rejected || 0) + 1;
          else if (verdict === 'route_mimir') next.routed = (next.routed || 0) + 1;
          else next.applied = (next.applied || 0) + 1;
          return next;
        });
        const label = item.candidate?.invariant?.slice(0, 60) || item.target?.element || item.staging_id;
        const reasonLabel = extra?.reason ? ` (${DISTINCT_REASONS.find(r => r.value === extra.reason)?.label || extra.reason})` : '';
        const topicLabel = extra?.topic_id ? ` → ${TOPICS.find(t => t.id === extra.topic_id)?.label || extra.topic_id}` : '';
        const verbLabel = verdict === 'corroborate' ? 'Corroborated'
          : verdict === 'distinct' ? `Distinct${reasonLabel}`
          : verdict === 'refine' ? 'Refined (matched superseded)'
          : verdict === 'route_mimir' ? 'Routed to Mimir'
          : verdict === 'admit_topic' ? `Admitted${topicLabel}`
          : verdict === 'reject_noise' ? `Noise${reasonLabel}`
          : 'Deferred';
        showToast(`${verbLabel}: ${label}`);
      } else {
        showToast(response.error || `Failed to ${verdict}`, 'error');
      }
    } catch {
      showToast(`Failed to ${verdict}`, 'error');
    } finally {
      setIsActioning(null);
      setInlineMode(null);
    }
  }, [removeCard, showToast, statusFilter]);

  const doSupersedeAction = useCallback(async (
    claim: SupersedeClaimItem,
    verdict: 'uphold_supersede' | 'decline_supersede',
    reason?: string
  ) => {
    setIsActioning(claim.staging_id);
    try {
      const response = await dedupApi.resolve({
        staging_id: claim.staging_id,
        verdict,
        reason,
      });
      if (response.success) {
        setSupersedeClaims(prev => prev.filter(c => c.staging_id !== claim.staging_id));
        setSupersedeCounts(prev => {
          const next = { ...prev };
          if (next.pending != null) next.pending = Math.max(0, (next.pending || 0) - 1);
          if (verdict === 'uphold_supersede') next.applied = (next.applied || 0) + 1;
          else next.rejected = (next.rejected || 0) + 1;
          return next;
        });
        const label = claim.target?.element || claim.staging_id;
        const verbLabel = verdict === 'uphold_supersede' ? 'Upheld' : 'Declined';
        showToast(`${verbLabel}: ${label}`);
      } else {
        showToast(response.error || `Failed to ${verdict}`, 'error');
      }
    } catch {
      showToast(`Failed to ${verdict}`, 'error');
    } finally {
      setIsActioning(null);
    }
  }, [showToast]);

  const doRetireMatched = useCallback(async (
    item: DedupReviewItem,
    reason: string
  ) => {
    if (!item.target?.pattern_id) {
      showToast('No matched standard to retire', 'error');
      return;
    }
    setIsActioning(item.staging_id);
    try {
      const retireResp = await standardsApi.retireStandard(
        item.target.pattern_id,
        `Retired from curation queue: ${reason}`
      );
      if (!retireResp.success) {
        showToast(retireResp.error || 'Failed to retire standard', 'error');
        setIsActioning(null);
        setInlineMode(null);
        return;
      }
      const rejectResp = await dedupApi.resolve({
        staging_id: item.staging_id,
        verdict: 'reject_noise',
        reason: `matched-standard-retired:${item.target.pattern_id}`,
      });
      if (rejectResp.success) {
        removeCard(item.staging_id);
        setCounts(prev => {
          const next = { ...prev };
          if (next.pending != null) next.pending = Math.max(0, (next.pending || 0) - 1);
          next.rejected = (next.rejected || 0) + 1;
          return next;
        });
      }
      showToast(`Retired ${item.target.pattern_id} + rejected candidate`);
    } catch {
      showToast('Retire failed', 'error');
    } finally {
      setIsActioning(null);
      setInlineMode(null);
    }
  }, [removeCard, showToast]);

  const doBatchAction = useCallback(async (
    verdict: 'corroborate' | 'distinct' | 'defer' | 'reject_noise',
    targetItems?: DedupReviewItem[]
  ) => {
    const targets = targetItems || filtered.filter(item => selected.has(item.staging_id));
    if (targets.length === 0) return;
    setIsActioning('batch');
    let succeeded = 0;
    for (const item of targets) {
      try {
        const payload: Record<string, string> = {
          staging_id: item.staging_id,
          verdict,
        };
        if (verdict === 'defer') {
          payload.defer_reason = 'batch-deferred via curation queue';
        }
        if (verdict === 'distinct') {
          payload.reason = 'genuinely_new';
        }
        if (verdict === 'reject_noise') {
          payload.reason = 'session_chatter';
        }
        const response = await dedupApi.resolve(payload as any);
        if (response.success) {
          removeCard(item.staging_id);
          succeeded++;
        }
      } catch { /* continue */ }
    }
    setIsActioning(null);
    const verbLabel = verdict === 'corroborate' ? 'Corroborated'
      : verdict === 'distinct' ? 'Admitted'
      : verdict === 'reject_noise' ? 'Rejected'
      : 'Deferred';
    showToast(`${verbLabel} ${succeeded}/${targets.length} items`);
    if (viewMode === 'source') loadQueue();
  }, [selected, filtered, removeCard, showToast, viewMode]);

  const scrollToCard = useCallback((index: number) => {
    const el = cardRefs.current.get(index);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  // Keyboard handler
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (inlineMode === 'distinct') {
        const reason = DISTINCT_REASONS.find(r => r.key === e.key);
        if (reason && filtered[focusIndex]) {
          e.preventDefault();
          doAction(filtered[focusIndex], 'distinct', { reason: reason.value });
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setInlineMode(null); return; }
        return;
      }

      if (inlineMode === 'defer') {
        const preset = DEFER_PRESETS.find(p => p.key === e.key);
        if (preset && filtered[focusIndex]) {
          e.preventDefault();
          const deferDate = new Date();
          deferDate.setDate(deferDate.getDate() + preset.days);
          doAction(filtered[focusIndex], 'defer', {
            defer_reason: `deferred until ${deferDate.toISOString().slice(0, 10)}`,
            defer_until: deferDate.toISOString(),
          });
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setInlineMode(null); return; }
        return;
      }

      if (inlineMode === 'topic') {
        const topic = TOPICS.find(t => t.key === e.key);
        if (topic && filtered[focusIndex]) {
          e.preventDefault();
          doAction(filtered[focusIndex], 'admit_topic', { topic_id: topic.id });
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setInlineMode(null); return; }
        return;
      }

      if (inlineMode === 'noise') {
        const nr = NOISE_REASONS.find(r => r.key === e.key);
        if (nr && filtered[focusIndex]) {
          e.preventDefault();
          doAction(filtered[focusIndex], 'reject_noise', { reason: nr.value });
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setInlineMode(null); return; }
        return;
      }

      if (inlineMode === 'retire') {
        const rr = RETIRE_REASONS.find(r => r.key === e.key);
        if (rr && filtered[focusIndex]) {
          e.preventDefault();
          doRetireMatched(filtered[focusIndex], rr.value);
          return;
        }
        if (e.key === 'Escape') { e.preventDefault(); setInlineMode(null); return; }
        return;
      }

      const focusedItem = filtered[focusIndex];
      const va = focusedItem ? availableVerdicts(focusedItem, statusFilter) : null;

      switch (e.key) {
        case 'j': {
          e.preventDefault();
          const next = Math.min(focusIndex + 1, filtered.length - 1);
          setFocusIndex(next);
          scrollToCard(next);
          break;
        }
        case 'k': {
          e.preventDefault();
          const prev = Math.max(focusIndex - 1, 0);
          setFocusIndex(prev);
          scrollToCard(prev);
          break;
        }
        case ' ': {
          e.preventDefault();
          if (!focusedItem) break;
          setSelected(prev => {
            const next = new Set(prev);
            if (next.has(focusedItem.staging_id)) next.delete(focusedItem.staging_id);
            else next.add(focusedItem.staging_id);
            return next;
          });
          break;
        }
        case 'a': {
          e.preventDefault();
          if (e.shiftKey && selected.size > 0) {
            doBatchAction('corroborate');
          } else if (focusedItem && va) {
            if (va.allowed.includes('corroborate')) {
              doAction(focusedItem, 'corroborate');
            } else {
              showToast(va.reason.corroborate || 'Corroborate not available', 'error');
            }
          }
          break;
        }
        case 'r': {
          e.preventDefault();
          if (e.shiftKey && selected.size > 0) {
            doBatchAction('distinct');
          } else if (focusedItem && va) {
            if (va.allowed.includes('distinct')) {
              setInlineMode('distinct');
            } else {
              showToast(va.reason.distinct || 'Distinct not available', 'error');
            }
          }
          break;
        }
        case 'f': {
          e.preventDefault();
          if (focusedItem && va) {
            if (va.allowed.includes('refine')) {
              doAction(focusedItem, 'refine');
            } else {
              showToast(va.reason.refine || 'Refine not available', 'error');
            }
          }
          break;
        }
        case 'd': {
          e.preventDefault();
          if (e.shiftKey && selected.size > 0) {
            doBatchAction('defer');
          } else if (focusedItem && va) {
            if (va.allowed.includes('defer')) {
              setInlineMode('defer');
            } else {
              showToast(va.reason.defer || 'Defer not available', 'error');
            }
          }
          break;
        }
        case 'm': {
          e.preventDefault();
          if (focusedItem && va) {
            if (va.allowed.includes('route_mimir')) {
              doAction(focusedItem, 'route_mimir');
            } else {
              showToast(va.reason.route_mimir || 'Route not available', 'error');
            }
          }
          break;
        }
        case 't': {
          e.preventDefault();
          if (focusedItem && va) {
            if (va.allowed.includes('admit_topic')) {
              setInlineMode('topic');
            } else {
              showToast(va.reason.admit_topic || 'Topic admission not available', 'error');
            }
          }
          break;
        }
        case 'n': {
          e.preventDefault();
          if (e.shiftKey && selected.size > 0) {
            doBatchAction('reject_noise');
          } else if (focusedItem && va) {
            if (va.allowed.includes('reject_noise')) {
              setInlineMode('noise');
            } else {
              showToast(va.reason.reject_noise || 'Reject not available', 'error');
            }
          }
          break;
        }
        case 'g': {
          e.preventDefault();
          if (focusedItem && focusedItem.target?.pattern_id && va) {
            setInlineMode('retire');
          } else {
            showToast('No matched standard to retire', 'error');
          }
          break;
        }
        case 'x': {
          e.preventDefault();
          if (!focusedItem) break;
          setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(focusedItem.staging_id)) next.delete(focusedItem.staging_id);
            else next.add(focusedItem.staging_id);
            return next;
          });
          break;
        }
        case 'Escape': {
          e.preventDefault();
          if (selected.size > 0) setSelected(new Set());
          break;
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusIndex, filtered, inlineMode, selected, doAction, doBatchAction, doRetireMatched, scrollToCard, showToast, statusFilter]);

  useEffect(() => {
    if (focusIndex >= filtered.length && filtered.length > 0) {
      setFocusIndex(filtered.length - 1);
    }
  }, [filtered.length, focusIndex]);

  if (isLoading && items.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4" ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Curation Queue</h1>
          {!supersedeMode && (
            <Badge color="info">{filtered.length} {statusFilter}</Badge>
          )}
          {supersedeMode && (
            <Badge color="pink">{supersedeClaims.length} supersede claim{supersedeClaims.length !== 1 ? 's' : ''}</Badge>
          )}
          {!supersedeMode && counts.pending != null && statusFilter !== 'pending' && (
            <Badge color="warning">{counts.pending} pending</Badge>
          )}
          {selected.size > 0 && (
            <Badge color="purple">{selected.size} selected</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setSupersedeMode(false); setStatusFilter('pending'); }}
            className={`px-3 py-1.5 text-sm rounded-l-md border ${
              !supersedeMode
                ? 'bg-blue-50 text-blue-700 border-blue-300'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Dedup Review
          </button>
          <button
            onClick={() => { setSupersedeMode(true); setStatusFilter('pending'); }}
            className={`px-3 py-1.5 text-sm rounded-r-md border-t border-r border-b ${
              supersedeMode
                ? 'bg-pink-50 text-pink-700 border-pink-300'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Supersede{supersedeCounts.pending ? ` (${supersedeCounts.pending})` : ''}
          </button>
          {!supersedeMode && (
            <>
              <span className="text-gray-300 ml-2">|</span>
              <button
                onClick={() => setViewMode('flat')}
                className={`px-2 py-1.5 text-xs rounded-l border ${
                  viewMode === 'flat'
                    ? 'bg-gray-100 text-gray-800 border-gray-300 font-medium'
                    : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Flat
              </button>
              <button
                onClick={() => setViewMode('source')}
                className={`px-2 py-1.5 text-xs rounded-r border-t border-r border-b ${
                  viewMode === 'source'
                    ? 'bg-gray-100 text-gray-800 border-gray-300 font-medium'
                    : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                }`}
              >
                By Source
              </button>
            </>
          )}
          {!supersedeMode && (
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="text-sm border-gray-300 rounded-md ml-2"
            >
              <option value="pending">Pending{counts.pending ? ` (${counts.pending})` : ''}</option>
              <option value="pending_admission">Admission{counts.pending_admission ? ` (${counts.pending_admission})` : ''}</option>
              <option value="deferred">Deferred{counts.deferred ? ` (${counts.deferred})` : ''}</option>
              <option value="applied">Applied{counts.applied ? ` (${counts.applied})` : ''}</option>
              <option value="routed">Routed{counts.routed ? ` (${counts.routed})` : ''}</option>
              <option value="rejected">Rejected{counts.rejected ? ` (${counts.rejected})` : ''}</option>
            </select>
          )}
          {supersedeMode && (
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="text-sm border-gray-300 rounded-md ml-2"
            >
              <option value="pending">Pending{supersedeCounts.pending ? ` (${supersedeCounts.pending})` : ''}</option>
              <option value="applied">Upheld{supersedeCounts.applied ? ` (${supersedeCounts.applied})` : ''}</option>
              <option value="rejected">Declined{supersedeCounts.rejected ? ` (${supersedeCounts.rejected})` : ''}</option>
            </select>
          )}
          {!supersedeMode && (typeCounts.dedup_review > 0 || typeCounts.soak_review > 0) && (
            <>
              <span className="text-gray-300 ml-2">|</span>
              <button
                onClick={() => setTypeFilter('all')}
                className={`px-2 py-1.5 text-xs rounded-l border ${
                  typeFilter === 'all'
                    ? 'bg-gray-100 text-gray-800 border-gray-300 font-medium'
                    : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setTypeFilter('dedup_review')}
                className={`px-2 py-1.5 text-xs border-t border-b ${
                  typeFilter === 'dedup_review'
                    ? 'bg-blue-50 text-blue-700 border-blue-300 font-medium'
                    : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Dedup{typeCounts.dedup_review ? ` (${typeCounts.dedup_review})` : ''}
              </button>
              <button
                onClick={() => setTypeFilter('soak_review')}
                className={`px-2 py-1.5 text-xs rounded-r border ${
                  typeFilter === 'soak_review'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300 font-medium'
                    : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Admission{typeCounts.soak_review ? ` (${typeCounts.soak_review})` : ''}
              </button>
            </>
          )}
          <Button size="xs" color="light" onClick={loadQueue} disabled={isLoading}>
            <HiRefresh className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Source stats + filter */}
      {!supersedeMode && Object.keys(sourceStats).length > 0 && (
        <div className="flex items-center gap-2 text-sm flex-wrap">
          <button
            onClick={() => setSourceFilter('all')}
            className={`px-2 py-1 rounded text-xs font-medium ${
              sourceFilter === 'all' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
            }`}
          >
            All ({items.length})
          </button>
          {Object.entries(sourceStats).map(([src, count]) => (
            <button
              key={src}
              onClick={() => setSourceFilter(src)}
              className={`px-2 py-1 rounded text-xs font-medium ${
                sourceFilter === src ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {src} ({count})
            </button>
          ))}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`p-3 rounded-md flex items-center gap-2 text-sm ${
          toast.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {toast.type === 'success' ? <HiCheck className="h-4 w-4" /> : <HiExclamation className="h-4 w-4" />}
          {toast.text}
        </div>
      )}
      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm flex items-center gap-2">
          <HiExclamation className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Verdict semantics — what each action does */}
      {!supersedeMode && (
        <div className="text-xs border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-1">
          <div className="flex items-center gap-4 flex-wrap">
            <span className="font-medium text-gray-600 w-16">Verdicts:</span>
            <span><kbd className="px-1 py-0.5 bg-green-100 rounded text-green-700 font-mono">a</kbd> <span className="text-green-700 font-medium">Corroborate</span> <span className="text-gray-500">— existing standard survives; candidate counts as corroboration only</span></span>
            <span><kbd className="px-1 py-0.5 bg-indigo-100 rounded text-indigo-700 font-mono">r</kbd> <span className="text-indigo-700 font-medium">Distinct</span> <span className="text-gray-500">— creates a new standard; both survive</span></span>
            <span><kbd className="px-1 py-0.5 bg-teal-100 rounded text-teal-700 font-mono">f</kbd> <span className="text-teal-700 font-medium">Refine</span> <span className="text-gray-500">— candidate becomes the standard; matched is superseded and preserved</span></span>
            <span><kbd className="px-1 py-0.5 bg-amber-100 rounded text-amber-700 font-mono">d</kbd> <span className="text-gray-500">Defer</span></span>
            <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">n</kbd> <span className="text-red-600 font-medium">Noise</span> <span className="text-gray-500">— marks rejected; admits nothing</span></span>
            <span><kbd className="px-1 py-0.5 bg-red-100 rounded text-red-700 font-mono">g</kbd> <span className="text-red-700 font-medium">Retire</span> <span className="text-gray-500">— retires matched standard + rejects candidate; both gone</span></span>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="font-medium text-gray-600 w-16">Nav:</span>
            <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">j</kbd>/<kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">k</kbd> navigate</span>
            <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">x</kbd> expand</span>
            <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">space</kbd> select</span>
            <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">m</kbd> route-to-mimir</span>
            <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-600 font-mono">t</kbd> admit-to-topic</span>
          </div>
          {statusFilter === 'pending_admission' && (
            <div className="text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
              Admission queue: pressing Corroborate on admission items creates a new standard (same as Distinct) — there is no existing standard to reinforce.
            </div>
          )}
        </div>
      )}

      {/* Batch action bar */}
      {!supersedeMode && selected.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <span className="text-sm font-medium text-purple-700">
            {selected.size} selected:
          </span>
          <span className="text-xs text-purple-600 max-w-lg truncate">
            {filtered.filter(item => selected.has(item.staging_id))
              .map(item => item.candidate?.invariant?.slice(0, 40) || item.target?.element || item.staging_id)
              .join(', ')}
          </span>
          <div className="ml-auto flex gap-2">
            {statusFilter === 'pending_admission' ? (
              <>
                <Button size="xs" color="success" onClick={() => doBatchAction('corroborate')} disabled={isActioning === 'batch'}>
                  Admit all ({selected.size})
                </Button>
                <Button size="xs" color="failure" onClick={() => doBatchAction('reject_noise')} disabled={isActioning === 'batch'}>
                  Reject all ({selected.size})
                </Button>
              </>
            ) : (
              <>
                <Button size="xs" color="success" onClick={() => doBatchAction('corroborate')} disabled={isActioning === 'batch'}>
                  Corroborate all ({selected.size})
                </Button>
                <Button size="xs" color="warning" onClick={() => doBatchAction('defer')} disabled={isActioning === 'batch'}>
                  Defer all ({selected.size})
                </Button>
              </>
            )}
            <Button size="xs" color="light" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Supersede loading */}
      {supersedeMode && isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size="xl" />
        </div>
      )}

      {/* Supersede Claims Cards */}
      {supersedeMode && !isLoading && supersedeClaims.length > 0 && (
        <div className="space-y-3">
          {supersedeClaims.map((claim, idx) => {
            const isFocused = idx === focusIndex;
            const isProcessing = isActioning === claim.staging_id;
            const fixtureClass = (claim.evidence as any)?.fixture_class;
            return (
              <div
                key={claim.staging_id}
                ref={el => { if (el) cardRefs.current.set(idx, el); }}
                onClick={() => setFocusIndex(idx)}
                className={`rounded-lg border p-4 transition-all cursor-pointer ${
                  isFocused
                    ? 'ring-2 ring-pink-400 border-pink-300 bg-pink-50/30'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  {fixtureClass && (
                    <Badge color="failure" className="text-xs font-bold uppercase tracking-wider animate-pulse">
                      FIXTURE
                    </Badge>
                  )}
                  <Badge color="pink" className="text-xs font-semibold">SUPERSEDE CLAIM</Badge>
                  <span className="text-xs text-gray-400">{claim.age_hours}h ago</span>
                  <span className="text-xs text-gray-400">by {claim.proposed_by}</span>
                  {claim.coordinated_by && claim.coordinated_at && (
                    <span className="text-xs text-gray-400 ml-auto">
                      Ruled by {claim.coordinated_by.replace('console:', '')} on{' '}
                      {new Date(claim.coordinated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>

                {claim.claim?.reason && (
                  <div className="mb-3 p-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
                    {claim.claim.reason}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-red-50 rounded p-3 border border-red-200">
                    <span className="text-xs font-medium text-red-700 uppercase tracking-wide block mb-1">
                      Target (being superseded)
                      {claim.target?.pattern_id && (
                        <span className="font-normal text-red-400 ml-1">({claim.target.pattern_id})</span>
                      )}
                    </span>
                    <p className="text-sm text-gray-800 font-medium">{claim.target?.element || '—'}</p>
                    <p className="text-sm text-gray-700 mt-1">{claim.target?.rule || '—'}</p>
                    {claim.target?.maturity && (
                      <span className="text-xs text-gray-400 mt-1 block">
                        {claim.target.maturity} · {claim.target.category}
                      </span>
                    )}
                  </div>
                  <div className="bg-green-50 rounded p-3 border border-green-200">
                    <span className="text-xs font-medium text-green-700 uppercase tracking-wide block mb-1">
                      Claimant (superseding)
                      {claim.claimant?.pattern_id && (
                        <span className="font-normal text-green-400 ml-1">({claim.claimant.pattern_id})</span>
                      )}
                    </span>
                    <p className="text-sm text-gray-800 font-medium">{claim.claimant?.element || '—'}</p>
                    <p className="text-sm text-gray-700 mt-1">{claim.claimant?.rule || '—'}</p>
                    {claim.claimant?.maturity && (
                      <span className="text-xs text-gray-400 mt-1 block">
                        {claim.claimant.maturity} · {claim.claimant.category}
                      </span>
                    )}
                  </div>
                </div>

                {claim.evidence && (
                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                    {(claim.evidence as any)?.cosine_score != null && (
                      <span>cosine: {formatScore((claim.evidence as any).cosine_score)}</span>
                    )}
                    {claim.claim?.claim_source && (
                      <span>origin: {claim.claim.claim_source}</span>
                    )}
                  </div>
                )}

                {fixtureClass && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-300 rounded-lg">
                    <span className="text-xs font-bold text-red-700 uppercase">
                      Test Fixture — {(claim.evidence as any)?.fixture_dispatch || 'unknown dispatch'}
                    </span>
                    {(claim.evidence as any)?.fixture_note && (
                      <p className="text-xs text-red-600 mt-0.5">{(claim.evidence as any).fixture_note}</p>
                    )}
                  </div>
                )}

                {statusFilter === 'pending' && !isProcessing && (
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      onClick={e => { e.stopPropagation(); doSupersedeAction(claim, 'uphold_supersede'); }}
                      className="px-3 py-1.5 text-xs rounded border border-green-300 bg-green-50 hover:bg-green-100 text-green-700 font-medium"
                    >
                      Uphold — demote target
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); doSupersedeAction(claim, 'decline_supersede', 'insufficient-evidence'); }}
                      className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium"
                    >
                      Decline — keep target
                    </button>
                  </div>
                )}

                {isProcessing && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                    <Spinner size="xs" /> Processing...
                  </div>
                )}

                {claim.defer_reason && claim.status === 'rejected' && (
                  <div className="mt-2 text-xs text-gray-500">
                    Reason: {claim.defer_reason.replace('supersede-declined:', '')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {supersedeMode && !isLoading && supersedeClaims.length === 0 && (
        <div className="bg-white rounded-lg border p-12 text-center">
          <HiCheck className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">No {statusFilter} supersede claims.</p>
        </div>
      )}

      {/* Grouped view loading */}
      {!supersedeMode && viewMode === 'source' && isLoading && (
        <div className="flex justify-center py-12">
          <Spinner size="xl" />
        </div>
      )}

      {/* Grouped view */}
      {!supersedeMode && viewMode === 'source' && !isLoading && sourceGroups.length > 0 && (
        <div className="space-y-3">
          {sourceGroups.map(group => {
            const isGroupExpanded = expandedGroups.has(group.source_project);
            const groupSelected = group.items.filter(i => selected.has(i.staging_id));
            const allGroupSelected = group.items.length > 0 && groupSelected.length === group.items.length;
            const someGroupSelected = groupSelected.length > 0 && !allGroupSelected;
            return (
              <div key={group.source_project} className="border border-gray-200 rounded-lg bg-white">
                <div
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedGroups(prev => {
                    const next = new Set(prev);
                    if (next.has(group.source_project)) next.delete(group.source_project);
                    else next.add(group.source_project);
                    return next;
                  })}
                >
                  <input
                    type="checkbox"
                    checked={allGroupSelected}
                    ref={el => { if (el) el.indeterminate = someGroupSelected; }}
                    onChange={e => {
                      e.stopPropagation();
                      setSelected(prev => {
                        const next = new Set(prev);
                        if (allGroupSelected) {
                          group.items.forEach(i => next.delete(i.staging_id));
                        } else {
                          group.items.forEach(i => next.add(i.staging_id));
                        }
                        return next;
                      });
                    }}
                    onClick={e => e.stopPropagation()}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  {isGroupExpanded
                    ? <HiChevronUp className="h-4 w-4 text-gray-400" />
                    : <HiChevronDown className="h-4 w-4 text-gray-400" />
                  }
                  <Badge color="indigo" className="text-xs font-semibold">{group.source_project}</Badge>
                  <span className="text-sm text-gray-600 font-medium">{group.item_count} items</span>
                  {group.oldest && (
                    <span className="text-xs text-gray-400">
                      {new Date(group.oldest).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      {group.newest && group.newest !== group.oldest && (
                        <> — {new Date(group.newest).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>
                      )}
                    </span>
                  )}
                  <div className="ml-auto flex gap-2" onClick={e => e.stopPropagation()}>
                    {statusFilter === 'pending_admission' ? (
                      <>
                        <button
                          onClick={() => doBatchAction('corroborate', group.items)}
                          disabled={isActioning === 'batch'}
                          className="px-2 py-1 text-xs rounded border border-green-300 bg-green-50 hover:bg-green-100 text-green-700 font-medium disabled:opacity-50"
                        >
                          Admit {group.item_count}
                        </button>
                        <button
                          onClick={() => doBatchAction('reject_noise', group.items)}
                          disabled={isActioning === 'batch'}
                          className="px-2 py-1 text-xs rounded border border-red-300 bg-red-50 hover:bg-red-100 text-red-700 font-medium disabled:opacity-50"
                        >
                          Reject {group.item_count}
                        </button>
                      </>
                    ) : (statusFilter === 'pending' && (
                      <>
                        <button
                          onClick={() => doBatchAction('corroborate', group.items)}
                          disabled={isActioning === 'batch'}
                          className="px-2 py-1 text-xs rounded border border-green-300 bg-green-50 hover:bg-green-100 text-green-700 font-medium disabled:opacity-50"
                        >
                          Corroborate {group.item_count}
                        </button>
                        <button
                          onClick={() => doBatchAction('defer', group.items)}
                          disabled={isActioning === 'batch'}
                          className="px-2 py-1 text-xs rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium disabled:opacity-50"
                        >
                          Defer {group.item_count}
                        </button>
                      </>
                    ))}
                  </div>
                </div>
                {isGroupExpanded && (
                  <div className="border-t border-gray-100 px-3 pb-3 pt-1 space-y-2">
                    {group.items.map(item => {
                      const isSelected = selected.has(item.staging_id);
                      const isProcessing = isActioning === item.staging_id || isActioning === 'batch';
                      const va = availableVerdicts(item, statusFilter);
                      const evidence: DedupEvidence | null = item.evidence || null;
                      return (
                        <div
                          key={item.staging_id}
                          className={`rounded border p-2 text-sm ${
                            isSelected ? 'border-purple-300 bg-purple-50/30' : 'border-gray-100 bg-gray-50/30'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setSelected(prev => {
                                  const next = new Set(prev);
                                  if (next.has(item.staging_id)) next.delete(item.staging_id);
                                  else next.add(item.staging_id);
                                  return next;
                                });
                              }}
                              className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-gray-800 truncate max-w-md font-medium">
                                  {item.candidate?.invariant?.slice(0, 80) || item.target?.element || '(unnamed)'}
                                </span>
                                {item.object_class && (
                                  <Badge color={item.object_class === 'claim' ? 'warning' : 'info'} className="text-xs uppercase">
                                    {item.object_class}
                                  </Badge>
                                )}
                                {evidence?.match_band && (
                                  <Badge color={bandColor(evidence.match_band)} className="text-xs">
                                    {evidence.match_band?.replace('_', ' ')} {formatScore(evidence.cosine_score)}
                                  </Badge>
                                )}
                                <span className="text-xs text-gray-400">{item.age_hours}h</span>
                              </div>
                              {item.target?.element && (
                                <p className="text-xs text-gray-500 mt-0.5 truncate">
                                  matched: {item.target.element}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                onClick={() => doAction(item, 'corroborate')}
                                disabled={isProcessing || !va.allowed.includes('corroborate')}
                                className="p-1 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
                                title={va.remapped.corroborate
                                  ? 'Corroborate — admission: creates new standard'
                                  : 'Corroborate — existing standard survives'}
                              >
                                <HiCheck className="h-3.5 w-3.5" />
                              </button>
                              {va.allowed.includes('refine') && (
                                <button
                                  onClick={() => doAction(item, 'refine')}
                                  disabled={isProcessing}
                                  className="p-1 rounded hover:bg-teal-100 text-teal-600 disabled:opacity-50"
                                  title="Refine — candidate becomes the standard; matched is superseded"
                                >
                                  <HiArrowSmUp className="h-3.5 w-3.5" />
                                </button>
                              )}
                              <button
                                onClick={() => doAction(item, statusFilter === 'pending_admission' ? 'reject_noise' : 'distinct',
                                  statusFilter === 'pending_admission' ? { reason: 'session_chatter' } : undefined)}
                                disabled={isProcessing || !va.allowed.includes(statusFilter === 'pending_admission' ? 'reject_noise' : 'distinct')}
                                className={`p-1 rounded disabled:opacity-50 ${
                                  statusFilter === 'pending_admission'
                                    ? 'hover:bg-red-100 text-red-600'
                                    : 'hover:bg-indigo-100 text-indigo-600'
                                }`}
                                title={statusFilter === 'pending_admission' ? 'Reject as noise' : 'Distinct — creates new standard'}
                              >
                                <HiPlusSm className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!supersedeMode && viewMode === 'source' && !isLoading && sourceGroups.length === 0 && (
        <div className="bg-white rounded-lg border p-12 text-center">
          <HiCheck className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">No {statusFilter} items in the queue.</p>
        </div>
      )}

      {/* Flat cards */}
      {!supersedeMode && viewMode === 'flat' && isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="xl" />
        </div>
      ) : !supersedeMode && viewMode === 'flat' && filtered.length === 0 ? (
        <div className="bg-white rounded-lg border p-12 text-center">
          <HiCheck className="mx-auto h-12 w-12 text-gray-300 mb-3" />
          <p className="text-sm text-gray-500">
            {items.length === 0
              ? `No ${statusFilter} items in the dedup review queue.`
              : 'No items match the current filter.'}
          </p>
        </div>
      ) : !supersedeMode && viewMode === 'flat' && (
        <div className="space-y-2">
          {filtered.map((item, idx) => {
            const isFocused = idx === focusIndex;
            const isSelected = selected.has(item.staging_id);
            const isExpanded = expanded.has(item.staging_id);
            const isProcessing = isActioning === item.staging_id || isActioning === 'batch';
            const va = availableVerdicts(item, statusFilter);
            const showDistinctPicker = isFocused && inlineMode === 'distinct';
            const showDeferPicker = isFocused && inlineMode === 'defer';
            const showTopicPicker = isFocused && inlineMode === 'topic';
            const showNoisePicker = isFocused && inlineMode === 'noise';
            const showRetirePicker = isFocused && inlineMode === 'retire';
            const evidence: DedupEvidence | null = item.evidence || null;
            const verdict = parseVerdictReason(item.verdict_reason);
            const fixtureClass = (evidence as any)?.fixture_class;
            const isIntraBatch = evidence?.intra_batch === true || (evidence as any)?.is_intra_batch === true;
            const intraBatchMatchId = (evidence as any)?.intra_batch_matched_id || evidence?.sibling_id;
            const siblingItem = intraBatchMatchId
              ? items.find(i => i.candidate?.candidate_id === intraBatchMatchId || (i as any).target?.pattern_id === intraBatchMatchId)
              : null;
            const siblingText = siblingItem?.candidate?.invariant || (evidence as any)?.sibling_text;

            return (
              <div
                key={item.staging_id}
                ref={el => { if (el) cardRefs.current.set(idx, el); }}
                onClick={() => setFocusIndex(idx)}
                className={`rounded-lg border p-3 transition-all cursor-pointer ${
                  isFocused
                    ? 'ring-2 ring-blue-400 border-blue-300 bg-blue-50/30'
                    : isSelected
                      ? 'border-purple-300 bg-purple-50/30'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {/* Main row */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      setSelected(prev => {
                        const next = new Set(prev);
                        if (next.has(item.staging_id)) next.delete(item.staging_id);
                        else next.add(item.staging_id);
                        return next;
                      });
                    }}
                    className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    tabIndex={-1}
                  />

                  <div className="flex-1 min-w-0">
                    {/* Header badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {fixtureClass && (
                        <Badge color="failure" className="text-xs font-bold uppercase tracking-wider animate-pulse">
                          FIXTURE
                        </Badge>
                      )}
                      <span className="text-sm font-semibold text-gray-900 truncate max-w-xs">
                        {item.candidate?.invariant?.slice(0, 80) || item.target?.element || '(unnamed)'}
                      </span>
                      {item.operation_type === 'soak_review' && (
                        <Badge color="success" className="text-xs">
                          Admission
                        </Badge>
                      )}
                      {item.operation_type === 'soak_review' && (
                        <span className="text-xs text-emerald-500">corroborate = admit as new</span>
                      )}
                      {item.object_class && (
                        <Badge color={item.object_class === 'claim' ? 'warning' : 'info'} className="text-xs uppercase">
                          {item.object_class}
                        </Badge>
                      )}
                      {evidence?.match_band && (
                        <>
                          <Badge color={bandColor(evidence.match_band)} className="text-xs">
                            {evidence.match_band?.replace('_', ' ')}
                          </Badge>
                          <Badge color="gray" className="text-xs">
                            {formatScore(evidence.cosine_score)}
                          </Badge>
                          <Badge color="gray" className="text-xs">
                            {methodBadge(evidence.dedup_method)}
                          </Badge>
                        </>
                      )}
                      {item.candidate?.extraction_class && (
                        <Badge color={item.candidate.extraction_class === 'RULING' ? 'purple' : 'blue'} className="text-xs">
                          {item.candidate.extraction_class}
                        </Badge>
                      )}
                      {isIntraBatch && (
                        <Badge color="pink" className="text-xs">intra-batch</Badge>
                      )}
                      {item.candidate?.source_label && (
                        <Badge color="indigo" className="text-xs">{item.candidate.source_label}</Badge>
                      )}
                      {verdict && (
                        <Badge color={verdictColor(verdict.verb)} className="text-xs font-semibold">
                          {verdictLabel(verdict.verb)}{verdict.detail ? ` · ${verdict.detail}` : ''}
                        </Badge>
                      )}
                      {item.age_hours > 0 && (
                        <span className="text-xs text-gray-400">{item.age_hours}h ago</span>
                      )}
                    </div>

                    {/* Resolution audit line for applied/deferred items */}
                    {item.coordinated_by && item.coordinated_at && (
                      <div className="mt-1 text-xs text-gray-400">
                        Resolved by {item.coordinated_by.replace('console:', '')} on{' '}
                        {new Date(item.coordinated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}{' '}
                        at {new Date(item.coordinated_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}

                    {/* Evidence summary: candidate vs matched */}
                    <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-amber-50 rounded p-2 border border-amber-100">
                        <span className="text-xs font-medium text-amber-700 uppercase tracking-wide block mb-1">Candidate</span>
                        <p className="text-gray-700 line-clamp-2">{item.candidate?.invariant || '—'}</p>
                        {item.candidate?.domain && (
                          <span className="text-xs text-gray-400 mt-1 block">domain: {item.candidate.domain}</span>
                        )}
                      </div>
                      {item.target?.element ? (
                      <div className="bg-blue-50 rounded p-2 border border-blue-100">
                        <span className="text-xs font-medium text-blue-700 uppercase tracking-wide block mb-1">
                          Matched Standard
                          {item.target?.pattern_id && (
                            <span className="font-normal text-blue-500 ml-1">({item.target.pattern_id})</span>
                          )}
                        </span>
                        <p className="text-gray-700 line-clamp-2">{item.target?.rule || '—'}</p>
                        {item.target?.maturity && (
                          <span className="text-xs text-gray-400 mt-1 block">
                            {item.target.maturity} · {item.target.category}
                          </span>
                        )}
                      </div>
                      ) : item.operation_type === 'soak_review' ? (
                      <div className="bg-emerald-50 rounded p-2 border border-emerald-100">
                        <span className="text-xs font-medium text-emerald-700 uppercase tracking-wide block mb-1">
                          New Standard
                        </span>
                        <p className="text-emerald-600 text-sm">No existing match — evaluate on its own merits</p>
                      </div>
                      ) : (
                      <div className="bg-amber-50 rounded p-2 border border-amber-200">
                        <span className="text-xs font-medium text-amber-700 uppercase tracking-wide block mb-1">
                          <HiExclamation className="h-3.5 w-3.5 inline -mt-0.5 mr-0.5" />
                          Matched Standard Not Found
                        </span>
                        <p className="text-amber-600 text-sm">Reference broken — target may be superseded or deleted</p>
                      </div>
                      )}
                    </div>

                    {/* Intra-batch sibling */}
                    {isIntraBatch && intraBatchMatchId && (
                      <div className="mt-2 bg-pink-50 rounded p-2 border border-pink-100">
                        <span className="text-xs font-medium text-pink-700 uppercase tracking-wide block mb-1">
                          Intra-Batch Sibling
                          <span className="font-normal text-pink-500 ml-1">({intraBatchMatchId})</span>
                        </span>
                        <p className="text-sm text-gray-700 line-clamp-3">{siblingText || '(sibling not in current view — expand for ID)'}</p>
                      </div>
                    )}

                    {/* Evidence detail row */}
                    {evidence && (
                      <div className="mt-1 flex items-center gap-3 text-xs text-gray-400">
                        <span>cosine: {formatScore(evidence.cosine_score)}</span>
                        {evidence.margin != null && <span>margin: {formatScore(evidence.margin)}</span>}
                        {evidence.unembedded_in_scope != null && evidence.unembedded_in_scope > 0 && (
                          <span className="text-amber-500">{evidence.unembedded_in_scope} unembedded in scope</span>
                        )}
                        <span>by: {item.proposed_by}</span>
                      </div>
                    )}
                  </div>

                  {/* Expand toggle */}
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      setExpanded(prev => {
                        const next = new Set(prev);
                        if (next.has(item.staging_id)) next.delete(item.staging_id);
                        else next.add(item.staging_id);
                        return next;
                      });
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    tabIndex={-1}
                  >
                    {isExpanded ? <HiChevronUp className="h-4 w-4" /> : <HiChevronDown className="h-4 w-4" />}
                  </button>

                  {/* Action buttons */}
                  <div className="flex gap-1 flex-shrink-0">
                    <button
                      onClick={e => { e.stopPropagation(); doAction(item, 'corroborate'); }}
                      disabled={isProcessing || !va.allowed.includes('corroborate')}
                      className="p-1.5 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
                      title={va.remapped.corroborate
                        ? 'Corroborate (a) — admission item: creates new standard (remapped to distinct)'
                        : 'Corroborate (a) — existing standard survives; candidate counted only'}
                      tabIndex={-1}
                    >
                      <HiCheck className="h-4 w-4" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setFocusIndex(idx); setInlineMode('distinct'); }}
                      disabled={isProcessing || !va.allowed.includes('distinct')}
                      className="p-1.5 rounded hover:bg-indigo-100 text-indigo-600 disabled:opacity-50"
                      title="Distinct (r) — creates new standard; both survive"
                      tabIndex={-1}
                    >
                      <HiPlusSm className="h-4 w-4" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); doAction(item, 'refine'); }}
                      disabled={isProcessing || !va.allowed.includes('refine')}
                      className="p-1.5 rounded hover:bg-teal-100 text-teal-600 disabled:opacity-50"
                      title={va.allowed.includes('refine')
                        ? 'Refine (f) — candidate becomes the standard; matched is superseded and preserved'
                        : va.reason.refine || 'Refine not available'}
                      tabIndex={-1}
                    >
                      <HiArrowSmUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); setFocusIndex(idx); setInlineMode('defer'); }}
                      disabled={isProcessing || !va.allowed.includes('defer')}
                      className="p-1.5 rounded hover:bg-amber-100 text-amber-600 disabled:opacity-50"
                      title="Defer (d) — revisit later; no admission"
                      tabIndex={-1}
                    >
                      <HiClock className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Fixture warning banner */}
                {fixtureClass && (
                  <div className="mt-2 ml-8 p-2 bg-red-50 border border-red-300 rounded-lg">
                    <span className="text-xs font-bold text-red-700 uppercase">
                      Test Fixture — {(evidence as any)?.fixture_dispatch || 'unknown dispatch'}
                    </span>
                    {(evidence as any)?.fixture_note && (
                      <p className="text-xs text-red-600 mt-0.5">{(evidence as any).fixture_note}</p>
                    )}
                  </div>
                )}

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="mt-3 ml-8 space-y-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded p-3 border border-gray-100">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Candidate Full Text</span>
                        <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{item.candidate?.invariant || '—'}</p>
                        {item.candidate?.rationale && (
                          <p className="text-xs text-gray-500 mt-2"><span className="font-medium">Rationale:</span> {item.candidate.rationale}</p>
                        )}
                        {item.candidate?.consequences && (
                          <p className="text-xs text-gray-500 mt-1"><span className="font-medium">Consequences:</span> {item.candidate.consequences}</p>
                        )}
                      </div>
                      <div className="bg-gray-50 rounded p-3 border border-gray-100">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Matched Standard Full Text</span>
                        <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{item.target?.rule || '—'}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          <span className="font-medium">ID:</span> {item.target?.pattern_id}
                          {item.target?.source && <> · <span className="font-medium">Source:</span> {item.target.source}</>}
                          {item.target?.domain && <> · <span className="font-medium">Domain:</span> {item.target.domain}</>}
                        </p>
                      </div>
                    </div>
                    {evidence && (
                      <div className="bg-gray-50 rounded p-3 border border-gray-100">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Evidence</span>
                        <div className="mt-1 grid grid-cols-4 gap-2 text-xs text-gray-600">
                          <div><span className="font-medium">Cosine:</span> {formatScore(evidence.cosine_score)}</div>
                          <div><span className="font-medium">Margin:</span> {evidence.margin != null ? formatScore(evidence.margin) : '—'}</div>
                          <div><span className="font-medium">Band:</span> {evidence.match_band}</div>
                          <div><span className="font-medium">Class:</span> {evidence.match_class}</div>
                          <div><span className="font-medium">Method:</span> {evidence.dedup_method}</div>
                          <div><span className="font-medium">Unembedded:</span> {evidence.unembedded_in_scope ?? '—'}</div>
                          {isIntraBatch && <div className="col-span-2"><span className="font-medium">Sibling:</span> {evidence.sibling_id || '—'}</div>}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Inline distinct picker */}
                {showDistinctPicker && (
                  <div className="mt-2 ml-8 p-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <span className="text-xs font-medium text-indigo-700 mb-1 block">
                      Mark as distinct — pick rationale (1-4, Esc to cancel):
                    </span>
                    <div className="flex gap-2">
                      {DISTINCT_REASONS.map(dr => (
                        <button
                          key={dr.value}
                          onClick={() => doAction(item, 'distinct', { reason: dr.value })}
                          className="px-2 py-1 text-xs rounded border border-indigo-200 hover:bg-indigo-100 text-indigo-700"
                        >
                          <kbd className="font-mono mr-1">{dr.key}</kbd>{dr.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inline defer picker */}
                {showDeferPicker && (
                  <div className="mt-2 ml-8 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <span className="text-xs font-medium text-amber-700 mb-1 block">
                      Defer until (1-4, Esc to cancel):
                    </span>
                    <div className="flex gap-2">
                      {DEFER_PRESETS.map(dp => {
                        const date = new Date();
                        date.setDate(date.getDate() + dp.days);
                        return (
                          <button
                            key={dp.key}
                            onClick={() => doAction(item, 'defer', {
                              defer_reason: `deferred until ${date.toISOString().slice(0, 10)}`,
                              defer_until: date.toISOString(),
                            })}
                            className="px-2 py-1 text-xs rounded border border-amber-200 hover:bg-amber-100 text-amber-700"
                          >
                            <kbd className="font-mono mr-1">{dp.key}</kbd>{dp.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Inline topic picker */}
                {showTopicPicker && (
                  <div className="mt-2 ml-8 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <span className="text-xs font-medium text-blue-700 mb-1 block">
                      Admit to topic — pick channel (1-{TOPICS.length}, Esc to cancel):
                    </span>
                    <div className="flex gap-2">
                      {TOPICS.map(tp => (
                        <button
                          key={tp.id}
                          onClick={() => doAction(item, 'admit_topic', { topic_id: tp.id })}
                          className="px-2 py-1 text-xs rounded border border-blue-200 hover:bg-blue-100 text-blue-700"
                        >
                          <kbd className="font-mono mr-1">{tp.key}</kbd>{tp.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inline noise picker */}
                {showNoisePicker && (
                  <div className="mt-2 ml-8 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <span className="text-xs font-medium text-red-700 mb-1 block">
                      Reject as noise — pick reason (1-{NOISE_REASONS.length}, Esc to cancel):
                    </span>
                    <div className="flex gap-2">
                      {NOISE_REASONS.map(nr => (
                        <button
                          key={nr.value}
                          onClick={() => doAction(item, 'reject_noise', { reason: nr.value })}
                          className="px-2 py-1 text-xs rounded border border-red-200 hover:bg-red-100 text-red-700"
                        >
                          <kbd className="font-mono mr-1">{nr.key}</kbd>{nr.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inline retire picker */}
                {showRetirePicker && (
                  <div className="mt-2 ml-8 p-2 bg-red-50 border border-red-300 rounded-lg">
                    <span className="text-xs font-medium text-red-700 mb-1 block">
                      Retire matched standard ({item.target?.pattern_id}) — both gone. Pick reason (1-{RETIRE_REASONS.length}, Esc to cancel):
                    </span>
                    <div className="flex gap-2">
                      {RETIRE_REASONS.map(rr => (
                        <button
                          key={rr.value}
                          onClick={() => doRetireMatched(item, rr.value)}
                          className="px-2 py-1 text-xs rounded border border-red-200 hover:bg-red-100 text-red-700"
                        >
                          <kbd className="font-mono mr-1">{rr.key}</kbd>{rr.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processing overlay */}
                {isProcessing && isActioning === item.staging_id && (
                  <div className="mt-2 ml-8 flex items-center gap-2 text-xs text-gray-500">
                    <Spinner size="xs" /> Processing...
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CurationQueue;
